import React, { createContext, useContext, ReactNode, useState } from 'react';
import { useConnection } from '@/hooks/use-connection';
import { AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';

interface ConnectionContextType {
  isConnected: boolean;
  isReconnecting: boolean;
  reconnect: () => Promise<void>;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const connection = useConnection();
  
  return (
    <ConnectionContext.Provider value={connection}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnectionContext() {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error('useConnectionContext must be used within a ConnectionProvider');
  }
  return context;
}

// This component shows a reconnection UI when the connection is lost
export function ConnectionStatus() {
  const { isConnected, isReconnecting, reconnect } = useConnectionContext();
  const [showHelp, setShowHelp] = useState(false);
  
  if (isConnected && !isReconnecting) {
    return null;
  }
  
  return (
    <div className="fixed top-4 right-4 z-50 bg-vibebops-card border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] p-4 rounded-lg max-w-xs">
      <div className="flex items-center gap-3">
        {isReconnecting ? (
          <RefreshCw className="w-5 h-5 text-amber-500 animate-spin" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-500" />
        )}
        <div>
          <p className="font-bold text-sm">
            {isReconnecting ? 'Reconnecting...' : 'Connection Lost'}
          </p>
          <p className="text-xs text-vibebops-textMuted">
            {isReconnecting 
              ? 'We are trying to restore your connection' 
              : 'Your connection to the server was lost'}
          </p>
        </div>
      </div>
      
      {!isReconnecting && (
        <>
          <button 
            onClick={() => reconnect()}
            className="mt-2 w-full py-1 px-2 text-sm font-bold rounded bg-vibebops-accent text-white hover:bg-vibebops-accent/90 transition-colors flex items-center justify-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Reconnect
          </button>
          
          <button 
            onClick={() => setShowHelp(prev => !prev)}
            className="mt-1 w-full py-1 px-2 text-xs text-center text-vibebops-textMuted hover:text-vibebops-text"
          >
            {showHelp ? 'Hide Help' : 'Need Help?'}
          </button>
          
          {showHelp && (
            <div className="mt-2 text-xs border-t pt-2 text-vibebops-textMuted">
              <p className="mb-1 font-medium">Try these fixes:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Check your internet connection</li>
                <li>Clear your browser cache</li>
                <li>
                  <button 
                    onClick={() => window.location.reload()}
                    className="text-vibebops-accent hover:underline inline-flex items-center gap-1"
                  >
                    Refresh the page
                  </button>
                </li>
                <li>
                  <a 
                    href="https://status.supabase.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-vibebops-accent hover:underline inline-flex items-center gap-1"
                  >
                    Check Supabase status
                    <ExternalLink className="w-2 h-2" />
                  </a>
                </li>
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
} 